import React, { useState } from 'react';
import { Box, Divider, Grid, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { FileRejection, useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { FormikProps } from 'formik';
import { toastStyles } from '../../utils/utils';
import AddNewCoachModal from '../AddNewCoach/AddNewCoachModal';
import StyledButton from '../Common/StyledButton';
import { IGroupClientFormValues } from '../../utils/validations/clientDetailsForm';

interface IUploadCSVProps {
  isOpen: boolean;
  onClose: () => void;
  formik: FormikProps<IGroupClientFormValues>;
}

export default function UploadCSV({ isOpen, onClose, formik }: IUploadCSVProps) {
  const { t } = useTranslation();
  const [uploadFile, setUploadedFile] = useState<File | null>(null);
  const [parsedData, setParsedData] = useState<
    { name: string; dob: string; email: string; phoneNumber: string }[]
  >([]);

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (rejectedFiles.length > 0) {
      toast.error(t('addNewClient.csvLimit'), toastStyles.error);
      return;
    }

    if (acceptedFiles.length > 0) {
      const file = acceptedFiles[0];
      if (file.size > 20 * 1024 * 1024) {
        toast.error(t('addNewClient.csvLimit'), toastStyles.error);
        return;
      }
      setUploadedFile(file);
      toast.success(t('addNewClient.csvSubmitted'));

      // Read and parse CSV file
      const reader = new FileReader();
      reader.onload = (event) => {
        const text = event.target?.result as string;
        if (text) {
          const rows = text.split('\n');
          const data = rows
            .slice(1) // Skip the header row
            .filter((row) => row.trim() !== '') // Filter out empty rows
            .map((row) => {
              const columns = row.split(',').map((col) => col.trim());
              return {
                name: columns[0] || '',
                dob: columns[1] || '',
                email: columns[2] || '',
                phoneNumber: columns[3] || '',
              };
            });
          setParsedData(data);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = () => {
    if (uploadFile && parsedData.length > 0) {
      let updatedMembers = [...formik.values.members, ...parsedData];
      updatedMembers = updatedMembers.filter(
        (member) => member.name || member.email || member.phoneNumber || member.dob
      );
      formik.setFieldValue('members', updatedMembers);
      onClose(); // Close the modal after submitting
    } else {
      toast.error(t('addNewClient.csvNotValid'), toastStyles.error);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    maxFiles: 1,
    accept: {
      'text/csv': [],
      'application/vnd.ms-excel': [],
    },
    maxSize: 20 * 1024 * 1024,
  });

  return (
    <AddNewCoachModal isOpen={isOpen}>
      <Grid item xs={12} bgcolor="background.paper" p="32px">
        <Grid gap="18px" display="flex" flexDirection="column">
          {/* first row */}
          <Box display="flex" justifyContent="space-between">
            <Typography variant="h4" color="primary">
              {t('addNewClient.uploadCSV')}
            </Typography>
            <img
              src="/assets/icons/close-circle-large.svg"
              alt="close"
              onClick={onClose}
              role="presentation"
              style={{ cursor: 'pointer' }}
            />
          </Box>
          <Divider />
          <Box>
            <Typography variant="bodyRegular" color="primary">
              {t(`addNewClient.uploadCSVDesc`)}
            </Typography>
          </Box>
          <Box display="flex" flexDirection="row" alignItems="center" gap="8px">
            <img src="/assets/icons/file-download.svg" alt="file-download" width={24} height={24} />
            <a
              href="assets/sample.csv"
              download="sample.csv"
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <Typography
                variant="subheadBold"
                sx={{
                  background: 'linear-gradient(137.65deg, #6599A5 24.11%, #3B667E 83.2%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  display: 'inline-block',
                  fontWeight: 'bold',
                }}
              >
                {t(`addNewClient.downloadCSV`)}
              </Typography>
            </a>
          </Box>
          <Box
            {...getRootProps()}
            sx={{
              width: '100%',
              height: '200px',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              border: '2px dashed #7CAEBE80',
              borderRadius: '10px',
              padding: '16px',
              marginTop: '8px',
              cursor: 'pointer',
            }}
          >
            <input {...getInputProps()} />
            <img src="/assets/icons/cloud-upload.svg" alt="upload-icon" width={32} height={32} />
            {uploadFile ? (
              <Typography variant="bodyRegular" color="primary">
                {uploadFile.name}
              </Typography>
            ) : (
              <Typography variant="bodyRegular" color="#7CAEBE">
                {t('addNewClient.uploadCSVFile')}
              </Typography>
            )}
          </Box>
          <Divider />
          <Box display="flex" justifyContent="flex-end">
            <StyledButton onClick={handleSubmit} disabled={!uploadFile}>
              {t('addNewClient.submitCSV')}
            </StyledButton>
          </Box>
        </Grid>
      </Grid>
    </AddNewCoachModal>
  );
}
